<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { computed, ref } from 'vue';
import { Amplitude } from '@seede/amplitude';

useHead({
  title: 'Pricing Debug - Veeso AI',
  script: [
    {
      defer: true,
      src: 'https://cdn.jsdelivr.net/npm/@polar-sh/checkout@latest/dist/embed.global.js',
      'data-auto-init': '',
    },
  ],
});

const { user, isAuthenticated, getToken, initAuth, setToken } = useAuth();
const { api, request } = useApi();

const tokenInput = ref('');
const plan = ref<string>('');
const isLoading = ref(false);
const error = ref('');
const lastResponse = ref<any>(null);
const checkoutUrl = ref('');
const checkoutIdInput = ref('');
const subscriptionPlans = ref<any[]>([]);

// Fetch available plans from API
if (import.meta.client) {
  request('/pricing/plans', {
    query: { debug: true },
  })
    .then((data: any) => {
      // Process plans data to extract unique plan keys
      const plans = new Set<string>();

      Object.keys(data).forEach((key) => {
        plans.add(key);
      });

      subscriptionPlans.value = Array.from(plans).map((p) => ({
        value: p,
        label: p,
      }));

      if (subscriptionPlans.value.length > 0 && !plan.value) {
        plan.value = subscriptionPlans.value[0].value;
      }
    })
    .catch((err) => {
      console.error('Failed to fetch pricing plans:', err);
    });
}

if (import.meta.client) {
  tokenInput.value = getToken() || '';
}

const lastResponseText = computed(() => {
  if (!lastResponse.value) return '';
  try {
    return JSON.stringify(lastResponse.value, null, 2);
  } catch {
    return String(lastResponse.value);
  }
});

async function saveToken() {
  error.value = '';
  const token = tokenInput.value.trim();
  if (!token) {
    error.value = 'Token 不能为空';
    return;
  }
  setToken(token);
  initAuth();
}

async function createPolarCheckout(redirect: boolean) {
  error.value = '';
  lastResponse.value = null;
  isLoading.value = true;
  try {
    const successUrl = import.meta.client
      ? `${window.location.origin}/payment/success?checkout_id={CHECKOUT_ID}`
      : undefined;
    const res = await request('/v1/checkout/polar', {
      method: 'POST',
      body: {
        plan: plan.value,
        return: 'json',
        ...(successUrl ? { successUrl } : {}),
      },
    });
    lastResponse.value = res;
    const url = (res as any)?.url;
    const id = (res as any)?.id;
    if (id) checkoutIdInput.value = id;
    checkoutUrl.value = typeof url === 'string' ? url : '';
    if (redirect && import.meta.client && typeof url === 'string' && url) {
      Amplitude.event('checkout_initiated', {
        source: 'landing_pricing_dev_page',
        provider: 'stripe',
        plan_id: plan.value,
        plan_key: plan.value,
        // Strip URL query/path — only host is safe (the rest may carry
        // user email, session token or return_to with PII).
        checkout_host: (() => {
          try {
            return new URL(url).host;
          } catch {
            return '';
          }
        })(),
        success_host: (() => {
          try {
            return successUrl ? new URL(successUrl).host : '';
          } catch {
            return '';
          }
        })(),
        page_path: window.location.pathname,
      });
      window.location.href = url;
    }
  } catch (e: any) {
    const msg = e?.data?.error || e?.response?._data?.error || e?.message || '请求失败';
    error.value = String(msg);
  } finally {
    isLoading.value = false;
  }
}

async function createStripeCheckout(redirect: boolean) {
  error.value = '';
  lastResponse.value = null;
  isLoading.value = true;
  try {
    const successUrl = import.meta.client
      ? `${window.location.origin}/payment/success?checkout_id={CHECKOUT_SESSION_ID}`
      : undefined;
    const res = await request('/checkout', {
      method: 'POST',
      body: {
        provider: 'stripe',
        plan: plan.value,
        return: 'json',
        ...(successUrl ? { successUrl } : {}),
      },
    });
    lastResponse.value = res;
    const url = (res as any)?.url;
    const id = (res as any)?.id;
    if (id) checkoutIdInput.value = id;
    checkoutUrl.value = typeof url === 'string' ? url : '';
    if (redirect && import.meta.client && typeof url === 'string' && url) {
      window.location.href = url;
    }
  } catch (e: any) {
    const msg = e?.data?.error || e?.response?._data?.error || e?.message || '请求失败';
    error.value = String(msg);
  } finally {
    isLoading.value = false;
  }
}

async function getCheckout() {
  error.value = '';
  lastResponse.value = null;
  const id = checkoutIdInput.value.trim();
  if (!id) {
    error.value = 'Checkout ID 不能为空';
    return;
  }
  isLoading.value = true;
  try {
    const res = await request(`/checkout/${id}`);
    lastResponse.value = res;
  } catch (e: any) {
    const msg = e?.data?.error || e?.response?._data?.error || e?.message || '请求失败';
    error.value = String(msg);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen pt-[60px] relative bg-[#fafafa]">
    <!-- Header Section -->
    <AppNav active-path="/pricing" />

    <!-- Main Content -->
    <div class="container mx-auto px-4 py-16 relative z-10">
      <!-- Hero Section -->
      <div class="max-w-[1280px] mx-auto mb-16">
        <div class="text-center mb-16">
          <h1 class="text-[4rem] md:text-[5rem] font-semibold leading-[1.1] text-black/90 mb-4">
            Pricing Plan's Coming Soon
          </h1>
          <p class="text-lg md:text-xl text-black/60 font-normal">
            we are working on the pricing plan, please check back later.
          </p>
        </div>

        <div
          class="rounded-[16px] bg-white/80 backdrop-blur-sm shadow-2xl border border-black/5 p-6"
        >
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 class="text-xl font-semibold text-black/90">支付测试 (Polar / Stripe)</h2>
            <div class="text-xs text-black/40">API: {{ api }}</div>
          </div>

          <div class="mt-6 grid gap-6 md:grid-cols-2">
            <div class="space-y-5">
              <div class="text-sm text-black/60">
                <span class="font-medium text-black/80">登录状态：</span>
                <span>{{ isAuthenticated ? '已登录' : '未登录' }}</span>
                <span v-if="user" class="ml-3 text-black/40">{{ user.email || user.id }}</span>
              </div>

              <div v-if="!isAuthenticated" class="space-y-2">
                <div class="text-sm text-black/60">JWT Token</div>
                <input
                  v-model="tokenInput"
                  class="w-full rounded-[12px] border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-black/30"
                  placeholder="粘贴 JWT token（Bearer 后面的部分）"
                />
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-[12px] bg-black/90 px-4 py-2 text-sm font-medium text-white hover:bg-black"
                  @click="saveToken"
                >
                  保存 Token
                </button>
              </div>

              <div class="space-y-2">
                <div class="text-sm text-black/60">Plan</div>
                <select
                  v-model="plan"
                  class="w-full rounded-[12px] border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-black/30"
                >
                  <optgroup label="Subscription (API)">
                    <option v-for="p in subscriptionPlans" :key="p.value" :value="p.value">
                      {{ p.label }}
                    </option>
                  </optgroup>
                </select>
              </div>

              <div class="flex flex-wrap gap-3">
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-[12px] bg-black/5 px-4 py-2 text-sm font-medium text-black/80 hover:bg-black/10 disabled:opacity-50"
                  :disabled="isLoading"
                  @click="createPolarCheckout(false)"
                >
                  创建 Checkout
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-[12px] bg-black/90 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-50"
                  :disabled="isLoading"
                  @click="createPolarCheckout(true)"
                >
                  创建并跳转
                </button>
              </div>

              <div class="flex flex-wrap gap-3 pt-4 border-t border-black/10">
                <div class="w-full text-xs font-medium text-black/60">Stripe Checkout</div>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-[12px] bg-[#635bff]/10 px-4 py-2 text-sm font-medium text-[#635bff] hover:bg-[#635bff]/20 disabled:opacity-50"
                  :disabled="isLoading"
                  @click="createStripeCheckout(false)"
                >
                  创建 Checkout
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-[12px] bg-[#635bff] px-4 py-2 text-sm font-medium text-white hover:bg-[#4b45c2] disabled:opacity-50"
                  :disabled="isLoading"
                  @click="createStripeCheckout(true)"
                >
                  创建并跳转
                </button>
              </div>

              <div class="space-y-2 pt-4 border-t border-black/10">
                <div class="text-sm text-black/60">Get Checkout Details</div>
                <div class="flex gap-2">
                  <input
                    v-model="checkoutIdInput"
                    class="flex-1 rounded-[12px] border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-black/30"
                    placeholder="Checkout ID"
                  />
                  <button
                    type="button"
                    class="inline-flex items-center justify-center rounded-[12px] bg-black/5 px-4 py-2 text-sm font-medium text-black/80 hover:bg-black/10 disabled:opacity-50"
                    :disabled="isLoading || !checkoutIdInput"
                    @click="getCheckout"
                  >
                    查询
                  </button>
                </div>
              </div>

              <div v-if="checkoutUrl" class="rounded-[12px] border border-black/10 bg-white p-3">
                <div class="text-xs font-medium text-black/60">Checkout URL</div>
                <div class="mt-1 break-all text-xs text-black/70">{{ checkoutUrl }}</div>
                <a
                  id="polar-embed-checkout-trigger"
                  class="mt-3 inline-flex items-center justify-center rounded-[12px] bg-black/90 px-4 py-2 text-sm font-medium text-white hover:bg-black"
                  :href="checkoutUrl"
                  data-polar-checkout
                  data-polar-checkout-theme="light"
                >
                  打开内嵌 Checkout
                </a>
              </div>

              <div v-if="error" class="text-sm text-red-600">{{ error }}</div>
            </div>

            <div class="rounded-[12px] bg-black/5 p-4">
              <div class="text-xs font-medium text-black/60">Last response</div>
              <pre
                class="mt-3 max-h-[320px] overflow-auto whitespace-pre-wrap text-xs text-black/70"
                >{{ lastResponseText }}</pre
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Styles are handled via Tailwind CSS classes */
</style>
